/* Outdated-browser toast (snippets/outdated-browser-banner.liquid).
 *
 * Shows a small dismissible toast when the browser is missing the CSS
 * generation this theme (Shopify Horizon) is built on, so shoppers on
 * stale browsers learn why the layout looks broken instead of blaming
 * the store. Born from a shopper stuck on Chrome 103 (2026-07-08):
 * gigantic product cards + misplaced nav carets, "incognito didn't help."
 *
 * Design constraints — read before editing:
 * - CLASSIC script, not a module, in conservative syntax: it must parse
 *   and run on exactly the old engines it exists to warn.
 * - WHETHER to show is decided by FEATURE DETECTION (the truth: the
 *   features whose absence actually breaks the layout). User-agent
 *   parsing is used only for the friendly "you're on X, need Y" line
 *   and the per-vendor update link, and is skipped when not confident
 *   (Brave/Vivaldi masquerade as Chrome; iOS browsers are WebKit).
 * - The toast is built in JS with INLINE styles on purpose: modern
 *   browsers pay nothing (no CSS, no DOM), and on broken browsers the
 *   toast can't be wrecked by the very stylesheet features that failed.
 */
(function () {
  'use strict';

  var SNOOZE_KEY = 'outdated-browser-snooze-until';
  var SNOOZE_MS = 7 * 24 * 60 * 60 * 1000; // one week

  /* The CSS generation the theme's layout requires. All of these pass on
   * any evergreen browser from late 2023 on:
   * - :has()               Chrome 105 / Safari 15.4 / Firefox 121
   * - container queries    Chrome 105 / Safari 16   / Firefox 110
   * - relaxed CSS nesting  Chrome 120 / Safari 17.2 / Firefox 117
   * Anything that throws (no CSS.supports, no constructable stylesheets)
   * is old enough to fail the gates anyway. */
  function supportsThemeCss() {
    try {
      if (!window.CSS || !CSS.supports) return false;
      if (!CSS.supports('selector(:has(a))')) return false;
      if (!CSS.supports('container-type: inline-size')) return false;
      // Relaxed nesting can't be probed via CSS.supports — parse a rule
      // with a nested bare element selector and see if it survives.
      var sheet = new CSSStyleSheet();
      sheet.replaceSync('a{b{color:red}}');
      var outer = sheet.cssRules[0];
      return !!(outer && outer.cssRules && outer.cssRules.length === 1);
    } catch (error) {
      return false;
    }
  }

  /* Desktop-pattern UA sniffing only; order matters (Edge/Opera UAs also
   * contain "Chrome", every WebKit UA contains "Safari"). `minimum` is the
   * oldest version with every gated feature above. */
  var BROWSERS = [
    { pattern: /Edg\/(\d+)/, name: 'Microsoft Edge', minimum: '120', url: 'https://www.microsoft.com/edge/download' },
    { pattern: /OPR\/(\d+)/, name: 'Opera', minimum: '106', url: 'https://www.opera.com/download' },
    { pattern: /Firefox\/(\d+)/, name: 'Firefox', minimum: '121', url: 'https://support.mozilla.org/kb/update-firefox-latest-release' },
    { pattern: /Chrome\/(\d+)/, name: 'Chrome', minimum: '120', url: 'https://support.google.com/chrome/answer/95414' },
    { pattern: /Version\/(\d+(?:\.\d+)?)[^)]* Safari/, name: 'Safari', minimum: '17.2', url: 'https://support.apple.com/108382' },
  ];

  function detectBrowser() {
    var ua = navigator.userAgent || '';
    for (var i = 0; i < BROWSERS.length; i++) {
      var match = ua.match(BROWSERS[i].pattern);
      if (match) {
        return { name: BROWSERS[i].name, current: match[1], minimum: BROWSERS[i].minimum, url: BROWSERS[i].url };
      }
    }
    return null;
  }

  function snoozed() {
    try {
      return Date.now() < Number(localStorage.getItem(SNOOZE_KEY) || 0);
    } catch (error) {
      return false;
    }
  }

  function snooze() {
    try {
      localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_MS));
    } catch (error) {
      /* private mode without storage: toast just reappears next page */
    }
  }

  function readConfig() {
    var el = document.querySelector('script[data-outdated-browser-config]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (error) {
      return null;
    }
  }

  function fillTemplate(template, browser) {
    return template
      .replace(/\{browser\}/g, browser.name)
      .replace(/\{current\}/g, browser.current)
      .replace(/\{minimum\}/g, browser.minimum);
  }

  function styleOf(el, styles) {
    for (var key in styles) {
      if (Object.prototype.hasOwnProperty.call(styles, key)) el.style[key] = styles[key];
    }
  }

  function buildToast(config, browser) {
    var toast = document.createElement('div');
    toast.id = 'outdated-browser-banner';
    toast.setAttribute('role', 'status');
    styleOf(toast, {
      position: 'fixed',
      left: '16px',
      right: '16px',
      bottom: '16px',
      zIndex: '2147483000',
      maxWidth: '420px',
      display: 'flex',
      alignItems: 'flex-start',
      boxSizing: 'border-box',
      padding: '14px 16px',
      background: 'var(--color-background, #ffffff)',
      color: 'var(--color-foreground, #1a1a1a)',
      border: '2px solid var(--color-border, #d9d9d9)',
      boxShadow: '0 6px 24px rgba(0, 0, 0, 0.18)',
      font: 'inherit',
      fontSize: '0.875rem',
      lineHeight: '1.45',
      textAlign: 'left',
    });

    var body = document.createElement('div');
    styleOf(body, { flex: '1 1 auto', minWidth: '0' });

    var message = document.createElement('p');
    message.textContent = config.message;
    styleOf(message, { margin: '0', fontWeight: '700' });
    body.appendChild(message);

    if (browser && config.detail) {
      var detail = document.createElement('p');
      detail.textContent = fillTemplate(config.detail, browser);
      styleOf(detail, { margin: '4px 0 0' });
      body.appendChild(detail);
    }

    if (browser && config.update_label) {
      var link = document.createElement('a');
      link.href = browser.url;
      link.target = '_blank';
      link.rel = 'noopener';
      link.textContent = fillTemplate(config.update_label, browser);
      styleOf(link, {
        display: 'inline-block',
        marginTop: '8px',
        color: 'inherit',
        fontWeight: '700',
        textDecoration: 'underline',
        textUnderlineOffset: '3px',
      });
      body.appendChild(link);
    }

    var dismiss = document.createElement('button');
    dismiss.type = 'button';
    dismiss.setAttribute('aria-label', config.dismiss_label || 'Close');
    dismiss.innerHTML = '&#215;';
    styleOf(dismiss, {
      flex: '0 0 auto',
      margin: '-6px -8px 0 8px',
      padding: '2px 10px',
      background: 'none',
      border: 'none',
      color: 'inherit',
      font: 'inherit',
      fontSize: '1.25rem',
      lineHeight: '1',
      cursor: 'pointer',
    });
    dismiss.addEventListener('click', function () {
      snooze();
      if (toast.parentNode) toast.parentNode.removeChild(toast);
    });

    toast.appendChild(body);
    toast.appendChild(dismiss);
    return toast;
  }

  function init() {
    if (document.getElementById('outdated-browser-banner')) return;
    if (snoozed() || supportsThemeCss()) return;
    var config = readConfig();
    if (!config || !config.message) return;
    document.body.appendChild(buildToast(config, detectBrowser()));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
